import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Loader2, RotateCcw, Save, Palette, Flame, Activity, Target, FolderTree } from 'lucide-react';
import { toast } from 'sonner';
import { vocabApi } from '../../lib/vocabularyApi';
import { useVocabulary } from '../../vocabulary/VocabularyProvider';

// Tailwind for the selected colour swatch.
function ColourSwatch({ hex }) {
  return (
    <span
      className="inline-block w-4 h-4 rounded-full border border-border"
      style={{ background: hex || 'transparent' }}
      aria-hidden="true"
    />
  );
}

// Compact colour picker: hex text + native colour input + intent dropdown.
function ColourField({ value, intentKey, intents, onChange }) {
  const linked = !!intentKey;
  const intentColor = linked
    ? (intents.find((i) => i.key === intentKey)?.color)
    : null;
  return (
    <div className="flex items-center gap-1.5">
      <ColourSwatch hex={linked ? intentColor : value} />
      <input
        type="color"
        value={linked ? (intentColor || '#000000') : (value || '#000000')}
        disabled={linked}
        onChange={(e) => onChange({ color: e.target.value, intent_key: null })}
        className="w-6 h-6 rounded border border-border cursor-pointer disabled:opacity-50"
        title={linked ? 'Disabled — colour inherited from intent' : 'Pick colour'}
      />
      <Input
        value={linked ? '' : (value || '')}
        disabled={linked}
        onChange={(e) => onChange({ color: e.target.value, intent_key: null })}
        placeholder={linked ? `via ${intentKey}` : '#hex'}
        className="h-7 w-24 text-[11px] font-mono"
      />
      <Select
        value={intentKey || '__none'}
        onValueChange={(v) => onChange({ intent_key: v === '__none' ? null : v })}
      >
        <SelectTrigger className="h-7 w-32 text-[11px]">
          <SelectValue placeholder="No intent" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none">— Manual hex —</SelectItem>
          {intents.map((i) => (
            <SelectItem key={i.key} value={i.key}>
              <span className="flex items-center gap-1.5">
                <ColourSwatch hex={i.color} /> {i.label}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Generic row editor used by each vocabulary table.
function VocabRow({ row, columns, intents, onSave }) {
  const [draft, setDraft] = useState(row);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(row);

  // Reset draft if upstream data changed (e.g. Restore Defaults).
  useEffect(() => { setDraft(row); }, [row]);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      await onSave(row.key, draft);
      toast.success(`Saved ${row.key}`);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr data-testid={`vocab-row-${row.key}`} className="border-b border-border/50 last:border-0">
      <td className="p-2">
        <code className="text-[10px] font-mono orka-text-tertiary">{row.key}</code>
      </td>
      {columns.map((col) => (
        <td key={col.field} className="p-2">
          {col.type === 'number' ? (
            <Input
              type="number"
              value={draft[col.field] ?? ''}
              onChange={(e) => set({ [col.field]: e.target.value === '' ? null : Number(e.target.value) })}
              className="h-7 w-20 text-[11px]"
            />
          ) : col.type === 'switch' ? (
            <Switch
              checked={!!draft[col.field]}
              onCheckedChange={(v) => set({ [col.field]: v })}
            />
          ) : (
            <Input
              value={draft[col.field] || ''}
              onChange={(e) => set({ [col.field]: e.target.value })}
              className="h-7 w-28 text-[11px]"
            />
          )}
        </td>
      ))}
      <td className="p-2">
        <ColourField
          value={draft.color}
          intentKey={draft.intent_key}
          intents={intents}
          onChange={set}
        />
      </td>
      <td className="p-2 text-right">
        <Button
          size="sm"
          variant="default"
          onClick={save}
          disabled={!dirty || busy}
          className="h-7 text-[11px]"
          data-testid={`vocab-save-${row.key}`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1" /> Save</>}
        </Button>
      </td>
    </tr>
  );
}

// Table for a single vocabulary kind.
function VocabTable({ kind, rows, columns, intents, onSaveRow, onRestore }) {
  return (
    <div data-testid={`vocab-table-${kind}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] orka-text-tertiary">
          {rows.length} rows · changes save per-row
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={onRestore}
          className="h-7 text-[11px]"
          data-testid={`vocab-restore-${kind}`}
        >
          <RotateCcw className="w-3 h-3 mr-1" /> Restore defaults
        </Button>
      </div>
      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted/40">
            <tr className="text-left text-[10px] uppercase tracking-wider orka-text-tertiary">
              <th className="p-2 font-semibold">Key</th>
              {columns.map((c) => (
                <th key={c.field} className="p-2 font-semibold">{c.label}</th>
              ))}
              <th className="p-2 font-semibold">Colour</th>
              <th className="p-2 text-right font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <VocabRow
                key={r.key}
                row={r}
                columns={columns}
                intents={intents}
                onSave={onSaveRow}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Intent table — separate component because intent rows don't have intent_key.
function IntentTable({ rows, onSaveRow, onRestore }) {
  return (
    <div data-testid="vocab-table-intent">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] orka-text-tertiary">
          {rows.length} intents · these are the source colours that other tables can inherit
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={onRestore}
          className="h-7 text-[11px]"
          data-testid="vocab-restore-intent"
        >
          <RotateCcw className="w-3 h-3 mr-1" /> Restore defaults
        </Button>
      </div>
      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted/40">
            <tr className="text-left text-[10px] uppercase tracking-wider orka-text-tertiary">
              <th className="p-2 font-semibold">Key</th>
              <th className="p-2 font-semibold">Label</th>
              <th className="p-2 font-semibold">Colour</th>
              <th className="p-2 text-right font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <IntentRow key={r.key} row={r} onSave={onSaveRow} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IntentRow({ row, onSave }) {
  const [draft, setDraft] = useState(row);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(row);
  useEffect(() => { setDraft(row); }, [row]);
  const save = async () => {
    setBusy(true);
    try {
      await onSave(row.key, { label: draft.label, color: draft.color });
      toast.success(`Saved ${row.key}`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <tr data-testid={`vocab-row-intent-${row.key}`} className="border-b border-border/50 last:border-0">
      <td className="p-2"><code className="text-[10px] font-mono orka-text-tertiary">{row.key}</code></td>
      <td className="p-2">
        <Input
          value={draft.label || ''}
          onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
          className="h-7 w-32 text-[11px]"
        />
      </td>
      <td className="p-2">
        <div className="flex items-center gap-1.5">
          <ColourSwatch hex={draft.color} />
          <input
            type="color"
            value={draft.color || '#000000'}
            onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))}
            className="w-6 h-6 rounded border border-border cursor-pointer"
          />
          <Input
            value={draft.color || ''}
            onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))}
            className="h-7 w-24 text-[11px] font-mono"
          />
        </div>
      </td>
      <td className="p-2 text-right">
        <Button size="sm" onClick={save} disabled={!dirty || busy} className="h-7 text-[11px]" data-testid={`vocab-save-intent-${row.key}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1" /> Save</>}
        </Button>
      </td>
    </tr>
  );
}

// Work areas + Personal sub-areas (re-uses the existing /api/org endpoints).
function AreasPanel({ refreshOuter }) {
  const [workAreas, setWorkAreas] = useState([]);
  const [subAreas, setSubAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wa, sa] = await Promise.all([
        fetch('/api/org/work-areas', { credentials: 'include' }).then((r) => r.json()),
        fetch('/api/org/personal-sub-areas', { credentials: 'include' }).then((r) => r.json()),
      ]);
      setWorkAreas(Array.isArray(wa) ? wa : []);
      setSubAreas(Array.isArray(sa) ? sa : []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveWorkArea = async (id, patch) => {
    const res = await fetch(`/api/org/work-areas/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
    toast.success('Saved');
    await load();
    refreshOuter?.();
  };
  const saveSubArea = async (id, patch) => {
    const res = await fetch(`/api/org/personal-sub-areas/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
    toast.success('Saved');
    await load();
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="vocab-areas-panel">
      <div>
        <h4 className="text-sm font-semibold mb-2">Work Areas</h4>
        <p className="text-[11px] orka-text-tertiary mb-3">{workAreas.length} areas</p>
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/40">
              <tr className="text-left text-[10px] uppercase tracking-wider orka-text-tertiary">
                <th className="p-2 font-semibold">Name</th>
                <th className="p-2 font-semibold">Colour</th>
                <th className="p-2 font-semibold">Personal?</th>
                <th className="p-2 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {workAreas.map((wa) => (
                <AreaRow key={wa.work_area_id} row={wa} idField="work_area_id" onSave={saveWorkArea} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold mb-2">Personal Sub-Areas</h4>
        <p className="text-[11px] orka-text-tertiary mb-3">
          {subAreas.length} sub-areas. The "Work" sub-area can link to a Work Area to surface tasks in both views.
        </p>
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/40">
              <tr className="text-left text-[10px] uppercase tracking-wider orka-text-tertiary">
                <th className="p-2 font-semibold">Name</th>
                <th className="p-2 font-semibold">Colour</th>
                <th className="p-2 font-semibold">Linked work area</th>
                <th className="p-2 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {subAreas.map((sa) => (
                <SubAreaRow
                  key={sa.sub_area_id}
                  row={sa}
                  workAreas={workAreas}
                  onSave={saveSubArea}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AreaRow({ row, onSave }) {
  const [draft, setDraft] = useState(row);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(row);
  useEffect(() => { setDraft(row); }, [row]);
  const save = async () => {
    setBusy(true);
    try {
      await onSave(row.work_area_id, {
        name: draft.name,
        color: draft.color,
        is_personal: draft.is_personal,
      });
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <tr data-testid={`vocab-row-wa-${row.work_area_id}`} className="border-b border-border/50 last:border-0">
      <td className="p-2">
        <Input
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          className="h-7 w-40 text-[11px]"
        />
      </td>
      <td className="p-2">
        <div className="flex items-center gap-1.5">
          <ColourSwatch hex={draft.color} />
          <input type="color" value={draft.color} onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))} className="w-6 h-6 rounded border border-border cursor-pointer" />
          <Input value={draft.color} onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))} className="h-7 w-24 text-[11px] font-mono" />
        </div>
      </td>
      <td className="p-2">
        <Switch checked={!!draft.is_personal} onCheckedChange={(v) => setDraft((d) => ({ ...d, is_personal: v }))} />
      </td>
      <td className="p-2 text-right">
        <Button size="sm" onClick={save} disabled={!dirty || busy} className="h-7 text-[11px]">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1" /> Save</>}
        </Button>
      </td>
    </tr>
  );
}

function SubAreaRow({ row, workAreas, onSave }) {
  const [draft, setDraft] = useState(row);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(row);
  useEffect(() => { setDraft(row); }, [row]);
  const save = async () => {
    setBusy(true);
    try {
      await onSave(row.sub_area_id, {
        name: draft.name,
        color: draft.color,
        linked_work_area_id: draft.linked_work_area_id || null,
      });
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <tr data-testid={`vocab-row-psa-${row.sub_area_id}`} className="border-b border-border/50 last:border-0">
      <td className="p-2">
        <Input
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          className="h-7 w-40 text-[11px]"
        />
      </td>
      <td className="p-2">
        <div className="flex items-center gap-1.5">
          <ColourSwatch hex={draft.color} />
          <input type="color" value={draft.color} onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))} className="w-6 h-6 rounded border border-border cursor-pointer" />
          <Input value={draft.color} onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))} className="h-7 w-24 text-[11px] font-mono" />
        </div>
      </td>
      <td className="p-2">
        <Select
          value={draft.linked_work_area_id || '__none'}
          onValueChange={(v) => setDraft((d) => ({ ...d, linked_work_area_id: v === '__none' ? null : v }))}
        >
          <SelectTrigger className="h-7 w-44 text-[11px]">
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">— None —</SelectItem>
            {workAreas.map((wa) => (
              <SelectItem key={wa.work_area_id} value={wa.work_area_id}>
                <span className="flex items-center gap-1.5">
                  <ColourSwatch hex={wa.color} /> {wa.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="p-2 text-right">
        <Button size="sm" onClick={save} disabled={!dirty || busy} className="h-7 text-[11px]">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1" /> Save</>}
        </Button>
      </td>
    </tr>
  );
}

// =============================================================================
// Main entry point — dropped into Settings.jsx as a TabsContent.
// =============================================================================
export default function VocabularySettingsPanel() {
  const { vocab, refresh, loading } = useVocabulary();
  const [activeTab, setActiveTab] = useState('priority');

  const intents = vocab?.intent || [];

  const saveRow = useCallback(async (kind, key, patch) => {
    await vocabApi.update(kind, key, patch);
    await refresh();
  }, [refresh]);

  const restore = useCallback(async (kind) => {
    if (!window.confirm(`Restore default ${kind} vocabulary? This wipes any custom edits for this org.`)) return;
    try {
      await vocabApi.restoreDefaults(kind);
      await refresh();
      toast.success(`Restored ${kind}`);
    } catch (e) { toast.error(e.message); }
  }, [refresh]);

  if (loading || !vocab) {
    return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  return (
    <Card className="max-w-5xl" data-testid="vocab-settings-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Palette className="w-4 h-4 text-violet-600" /> Vocabulary
          <Badge variant="outline" className="ml-2 text-[10px]">org-scoped</Badge>
        </CardTitle>
        <CardDescription>
          Edit the colours, labels and definitions for Priority, Effort, Status, Intent, and Work Areas.
          Set <em>Intent</em> on any row to inherit colour from that intent (great for consistent palettes across tables).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-5 w-full max-w-2xl mb-4" data-testid="vocab-tabs">
            <TabsTrigger value="priority" data-testid="vocab-tab-priority">
              <Flame className="w-3.5 h-3.5 mr-1" /> Priority
            </TabsTrigger>
            <TabsTrigger value="effort" data-testid="vocab-tab-effort">
              <Activity className="w-3.5 h-3.5 mr-1" /> Effort
            </TabsTrigger>
            <TabsTrigger value="status" data-testid="vocab-tab-status">
              <Target className="w-3.5 h-3.5 mr-1" /> Status
            </TabsTrigger>
            <TabsTrigger value="intent" data-testid="vocab-tab-intent">
              <Palette className="w-3.5 h-3.5 mr-1" /> Intent
            </TabsTrigger>
            <TabsTrigger value="areas" data-testid="vocab-tab-areas">
              <FolderTree className="w-3.5 h-3.5 mr-1" /> Areas
            </TabsTrigger>
          </TabsList>

          <TabsContent value="priority">
            <VocabTable
              kind="priority"
              rows={vocab.priority || []}
              intents={intents}
              columns={[
                { field: 'short_label', label: 'Short' },
                { field: 'long_label', label: 'Long label' },
              ]}
              onSaveRow={(key, patch) => saveRow('priority', key, patch)}
              onRestore={() => restore('priority')}
            />
          </TabsContent>

          <TabsContent value="effort">
            <VocabTable
              kind="effort"
              rows={vocab.effort || []}
              intents={intents}
              columns={[
                { field: 'short_label', label: 'Short' },
                { field: 'long_label', label: 'Long label' },
                { field: 'minutes', label: 'Minutes', type: 'number' },
              ]}
              onSaveRow={(key, patch) => saveRow('effort', key, patch)}
              onRestore={() => restore('effort')}
            />
          </TabsContent>

          <TabsContent value="status">
            <VocabTable
              kind="status"
              rows={vocab.status || []}
              intents={intents}
              columns={[
                { field: 'label', label: 'Label' },
                { field: 'short', label: 'Short' },
                { field: 'is_visible_in_picker', label: 'In picker', type: 'switch' },
              ]}
              onSaveRow={(key, patch) => saveRow('status', key, patch)}
              onRestore={() => restore('status')}
            />
          </TabsContent>

          <TabsContent value="intent">
            <IntentTable
              rows={vocab.intent || []}
              onSaveRow={(key, patch) => saveRow('intent', key, patch)}
              onRestore={() => restore('intent')}
            />
          </TabsContent>

          <TabsContent value="areas">
            <AreasPanel refreshOuter={refresh} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
